export const CARBONATE_LOGIC = {
  description: "Carbonates & Borates Identification Rules",
  
  coreRules: `
=== 碳酸盐矿物的终极鉴定法则 (Carbonates Diagnostic Protocol) ===

[4.1 钙镁铁碳酸盐基础轴 (The Calcite-Dolomite-Siderite Axis)]
*   **方解石 (Calcite)**: 碳酸盐的标杆。必须寻找三个特征之一：完美的菱面体解理 (Rhombohedral cleavage)、“犬牙状”晶体 (Dogtooth spar)，或极高的双折射率（透明晶体看过去会有重影）。它是滴酸起泡的标准参照物。
*   **白云石 (Dolomite)**: 伪装的方解石。核心破绽：它的晶面通常是**弯曲的**，常常形成特征性的**“马鞍状”晶体 (Saddle-shaped crystals)**，并且带有珍珠光泽或微弱的粉色调。
*   **菱铁矿 (Siderite)**: 铁的碳酸盐。如果看到类似方解石的菱面体，但颜色是**黄褐色、土褐色或深棕色**，并且晶面通常也是弯曲的，这就是菱铁矿。
*   **菱镁矿 (Magnesite)**: 镁的碳酸盐。如果它不是晶体，而是长得像**白色的瓷器 (Porcelain-like)**、致密块状或土状，或者如冰糖一般的白色晶体，优先考虑菱镁矿。

[4.2 艳丽的铜锰锌碳酸盐 (The Colorful Carbonates: Cu, Mn, Zn)]
*   **菱锰矿 (Rhodochrosite)**: 绝对的特征色——**玫瑰红、粉红色**。它可以是完美的粉色菱面体晶体（像极了粉色的方解石），也可以是带有同心层纹的钟乳状/葡萄状构造（印加玫瑰）。
*   **蓝铜矿 (Azurite) & 孔雀石 (Malachite)**: 铜的经典兄弟。
    *   **蓝铜矿**: 只要看到**极度深邃的深蓝色 (Deep Azure Blue)**，呈单斜柱状或厚板状晶体，立刻锁定。
    *   **孔雀石**: 只要看到**翠绿色 / 亮绿色 (Bright Green)**，呈典型的**葡萄状、钟乳状块体，或者放射状纤维结晶**，甚至具有深浅相间的条带，立刻锁定。两者经常共生，所谓“铜矿的红娘”。
*   **菱锌矿 (Smithsonite)**: 锌的伪装大师。通常呈现极其经典的**葡萄状/肾状块体 (Botryoidal/Reniform)**，表面具有油脂/玻璃至珍珠光泽。颜色多变（蓝、绿、粉、黄、灰），如果在铅锌矿床上看到半透明的葡萄状皮壳，首选菱锌矿。(俗称干骨矿 Dry Bone Ore)。

[4.3 重金属与奇异结构碳酸盐 (Heavy Metals & Exotic Forms)]
*   **文石 / 霰石 (Aragonite)**: 与方解石同质多象。必须寻找：由多个晶体穿插形成的**假六方柱状双晶 (Pseudo-hexagonal twins)**，或者像树枝/珊瑚一样的**铁花构造 (Flos ferri)**。
*   **白铅矿 (Cerussite)**: 铅的碳酸盐。质量极重。核心视觉锚点：**极度明亮的金刚光泽 (Adamantine luster)**，并且经常形成极其复杂的**“V”字形、星状或网格状穿插双晶 (Reticulated twins)**。
*   **绿铜锌矿 (Aurichalcite) vs 斜方绿铜锌矿/锌孔雀石 (Rosasite)**: 极易混淆的蓝绿色次生矿物。
    *   **绿铜锌矿 (Aurichalcite)**: 颜色偏**浅蓝、天蓝至淡蓝绿色**，通常呈现出**极其脆弱的、毛绒绒的针状/纤维状簇生 (Delicate acicular/fibrous tufts)**，看起来像一团极轻的蓝色绒毛。
    *   **斜方绿铜锌矿 (Rosasite)**: 颜色偏**深蓝绿色至翠绿色**，通常呈现为紧密的**葡萄状皮壳或半球状结核 (Botryoidal crusts)**，比绿铜锌矿显得更致密、坚实。
*   **碳酸镁铬矿 (Stichtite)**: 颜色极其特殊：**紫丁香色、粉紫色到浅紫红色 (Lilac/Purple)**。它几乎总是以斑块、云母状或皮壳状存在于**绿色的蛇纹岩 (Serpentinite)** 中。紫绿相间的组合是绝对的鉴定锚点。
*   **水纤菱镁矿 (Hydromagnesite)**: 通常为纯白色，外观像粉笔灰或白垩。如果放大看，能看到**极细的针状、毛发状晶体组成的放射状簇 (Radiating acicular tufts)**，或者皮壳状珍珠光泽斑点。
*   **天然碱 (Trona)**: 钠盐。灰白色至黄白色，通常为纤维状、柱状或块状。出现在干旱碱湖沉积中，带有苦咸味和极易溶于水的特征。
`,

  diagnosticTests: `
- 对于看似方解石但颜色怪异的晶体，检查其晶面是否微弯（白云石/菱铁矿）。
- 对于粉色菱面体，直接关联菱锰矿。
- 看到绿色蛇纹石中夹杂鲜艳的紫粉色云母状斑块，立刻判定为碳酸镁铬矿 (Stichtite)。
- 如果蓝绿色次生矿物呈现脆弱如毛发般的绒毛状，倾向于绿铜锌矿 (Aurichalcite)；如果是致密的葡萄状/半球形，倾向于锌孔雀石 (Rosasite)。
- 评估碳酸盐的光泽：如果有刺眼的金刚光泽并且呈现网状双晶，那不是方解石，而是极重的白铅矿。
`
};
